import type { ReactNode } from 'react';
import { ShieldOff } from 'lucide-react';
import { canAnywhere, type Permission } from '@hrms/shared';
import { EmptyState } from '@hrms/ui';
import { useAuthenticatedSession } from './SessionProvider.js';

/**
 * Renders children only for an actor holding `permission` at some scope — or, given a list, ANY of
 * them, matching `NavItem.permission` so a route and the sidebar entry that leads to it agree.
 *
 * A clear no-access state, NOT a 404. The API's 404-for-denials rule exists so an attacker
 * enumerating ids can't learn that a record exists — but this gate sits in front of an area, not
 * a record, for someone already signed in. Telling them plainly saves a helpdesk ticket, and the
 * route being reachable by URL leaks nothing the sidebar didn't already imply.
 *
 * Navigation gating only, same as visibleNav: the API is the boundary that matters.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission | readonly Permission[];
  children: ReactNode;
}) {
  const { actor } = useAuthenticatedSession();
  const any: readonly Permission[] = typeof permission === 'string' ? [permission] : permission;
  if (any.some((p) => canAnywhere(actor, p))) return children;
  return <NoAccess />;
}

export function NoAccess() {
  return (
    <EmptyState
      icon={<ShieldOff className="size-8" />}
      title="You don't have access to this"
      description="Your role doesn't include this area. If you need it, ask an HR admin to update your access."
    />
  );
}
